import { Loader2, CheckCircle2, HelpCircle, AlertTriangle, MessageSquareQuote, ScrollText, Scale, Flag } from "lucide-react";

// ---- Slogan chip ----
export function Slogan({ children, className = "" }) {
  return (
    <span className={`dl-slogan text-[10px] sm:text-xs text-gold/90 ${className}`}>{children}</span>
  );
}

// ---- Faint tactical play-diagram backdrop (Xs / Os / route arrows) ----
export function PlayBackdrop({ className = "" }) {
  return (
    <svg className={`pointer-events-none absolute inset-0 w-full h-full opacity-[0.08] ${className}`} aria-hidden="true">
      <defs>
        <marker id="dl-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8" fill="none" stroke="#F5C518" strokeWidth="1.2" />
        </marker>
      </defs>
      <path d="M40,180 C160,60 280,300 420,120" fill="none" stroke="#F5C518" strokeWidth="1.5" strokeDasharray="7 7" markerEnd="url(#dl-arrow)" />
      <path d="M120,320 C240,220 360,360 520,240" fill="none" stroke="#ffffff" strokeWidth="1.2" strokeDasharray="4 8" markerEnd="url(#dl-arrow)" />
      <g stroke="#ffffff" strokeWidth="1.5">
        <line x1="70" y1="70" x2="90" y2="90" /><line x1="90" y1="70" x2="70" y2="90" />
        <line x1="330" y1="250" x2="350" y2="270" /><line x1="350" y1="250" x2="330" y2="270" />
      </g>
      <circle cx="250" cy="130" r="12" fill="none" stroke="#F5C518" strokeWidth="1.5" />
      <circle cx="470" cy="300" r="12" fill="none" stroke="#ffffff" strokeWidth="1.5" />
    </svg>
  );
}

// ---- Stat tile ----
export function StatTile({ label, value, accent, testid }) {
  return (
    <div className="dl-panel rounded-md px-3 py-3 sm:px-4 sm:py-4" data-testid={testid}>
      <div className="label-mono text-[9px] sm:text-[10px]">{label}</div>
      <div className={`mt-1 font-display font-bold text-xl sm:text-2xl ${accent ? "text-gold" : "text-white"}`}>{value}</div>
    </div>
  );
}

// ---- Status pill for case seasons ----
const STATUS_MAP = {
  active: { label: "PREVIEW", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  live: { label: "ACTIVE", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  open: { label: "OPEN", cls: "bg-gold/15 text-gold border-gold/40" },
  coming_soon: { label: "COMING SOON", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  completed: { label: "COMPLETED", cls: "bg-white/10 text-white/60 border-white/20" },
  inactive: { label: "CLOSED", cls: "bg-white/10 text-white/50 border-white/20" },
};
export function StatusPill({ status, className = "" }) {
  const s = STATUS_MAP[status] || STATUS_MAP.inactive;
  return (
    <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[10px] font-mono font-semibold tracking-wider ${s.cls} ${className}`}>
      {s.label}
    </span>
  );
}

// ---- Evidence / testimony classification (icon + text + color — never color alone) ----
const CLASS_MAP = {
  FACT: { icon: CheckCircle2, cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  TESTIMONY: { icon: MessageSquareQuote, cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  ALLEGATION: { icon: ScrollText, cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  INFERENCE: { icon: Scale, cls: "text-violet-300 border-violet-500/40 bg-violet-500/10" },
  DISPUTED: { icon: AlertTriangle, cls: "text-red-300 border-red-500/40 bg-red-500/10" },
  UNKNOWN: { icon: HelpCircle, cls: "text-white/60 border-white/20 bg-white/5" },
};
export function ClassBadge({ cls, className = "" }) {
  const c = CLASS_MAP[cls] || CLASS_MAP.UNKNOWN;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-mono font-semibold tracking-wide ${c.cls} ${className}`}>
      <Icon className="w-3 h-3" /> {cls}
    </span>
  );
}

// ---- Flag on the play ----
export function FlagChip({ type, note, resolved }) {
  const label = (type || "").replace(/_/g, " ");
  const cls = resolved
    ? "border-white/20 bg-white/5 text-white/45"
    : "border-red-500/40 bg-red-500/10 text-red-300";
  return (
    <span title={note || label} className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-mono font-semibold tracking-wide ${cls}`}>
      <Flag className="w-3 h-3" /> {label}{resolved ? " · resolved" : ""}
    </span>
  );
}

// ---- Section header ----
export function SectionHeader({ kicker, title, desc, right, className = "" }) {
  return (
    <div className={`flex items-start justify-between gap-3 ${className}`}>
      <div>
        {kicker && <Slogan className="block mb-1">{kicker}</Slogan>}
        <h2 className="font-display font-bold text-xl sm:text-2xl text-white tracking-tight">{title}</h2>
        {desc && <p className="mt-1 text-sm text-white/55 max-w-2xl">{desc}</p>}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

// ---- Loading / Empty ----
export function DLLoading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-20 text-white/50" data-testid="dl-loading">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> <span className="label-mono">{label}</span>
    </div>
  );
}
export function EmptyState({ icon: Icon, title, desc, action, testid }) {
  return (
    <div className="dl-panel rounded-lg py-14 px-6 text-center flex flex-col items-center" data-testid={testid}>
      {Icon && <Icon className="w-8 h-8 text-white/25 mb-3" />}
      <div className="font-display font-semibold text-white text-lg">{title}</div>
      {desc && <p className="mt-1 text-sm text-white/50 max-w-md">{desc}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// ---- Permanent Defense League simulation disclaimer (verbatim, legal) ----
export function SimulationDisclaimer({ compact = false, className = "" }) {
  if (compact) {
    return (
      <div className={`flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-white/45 ${className}`} data-testid="dl-sim-disclaimer-compact">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-300/70 shrink-0" />
        Defense League Simulation · publicly available information only · Cap Points & Attorney Hours are simulation resources, not real funding
      </div>
    );
  }
  return (
    <div className={`dl-panel rounded-md border-l-2 border-l-amber-400/60 p-4 ${className}`} data-testid="dl-sim-disclaimer">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-300" />
        <span className="dl-slogan text-xs text-amber-200">Defense League Simulation</span>
      </div>
      <p className="text-xs text-white/55 leading-relaxed">
        This simulation uses publicly available information. Actual counsel may possess discovery, privileged
        communications, work product, expert findings, investigative material and other information unavailable to
        participants. A difference between a participant's strategy and the strategy later taken by actual counsel does
        not establish that either strategy was correct or incorrect. The exercise evaluates decision-making based upon
        the information available to the participant at the time.
      </p>
      <p className="mt-2 text-xs text-white/45">
        Cap Points and Attorney Hours are simulation resources and do not represent actual defense funding or expenditures.
      </p>
    </div>
  );
}

// ---- Buttons ----
export function DLButton({ children, variant = "primary", className = "", ...props }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-sm font-semibold text-sm px-4 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-gold text-charcoal hover:bg-gold-hover",
    ghost: "border border-white/15 text-white/85 hover:border-gold/50 hover:text-gold",
    danger: "border border-red-500/40 text-red-300 hover:bg-red-500/10",
    dark: "bg-white/5 border border-white/10 text-white/80 hover:bg-white/10",
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} {...props}>{children}</button>
  );
}
